import { SpecialOffer } from '@c/Catalog/Map/components/SpecialOffer'
import { FeedbackModal } from '@c/Feedback'
import { Header, LayoutCookie } from '@c/Layout'
import { PaymentModal } from '@c/Profile'
import { Loader } from '@c/Ui'
import { Capacitor } from '@capacitor/core'
import { Libraries, useJsApiLoader } from '@react-google-maps/api'
import Cookies from 'js-cookie'
import { nanoid } from 'nanoid'
import React, { useEffect, useRef, useState } from 'react'
import ReactGA from 'react-ga4'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

import { useGeolocation } from '@/core/hooks/useGeolocation'
import { useAppDispatch, useAppSelector } from '@/core/store'
import { HomePage } from '@/pages/Home.page'

import { FullScreenImage } from './components/Catalog/FullScreenImage'
import { CleverMapAccessModal } from './components/Catalog/Map/components/CleverMapAccessModal'
import { TutorialPrompt } from './components/Tutorial/TutorialPrompt'
import { TutorialSlides } from './components/Tutorial/TutorialSlides'

const libraries = ['marker'] as Libraries

const App: React.FC = () => {
  //remove the loading message
  useEffect(() => {
    // Remove the initial loader
    const loader = document.getElementById('initial-loader')
    if (loader) {
      loader.style.display = 'none'
    }
  }, [])

  const { modal } = useAppSelector((store) => store.uiState)
  const { interestPoints, cleverMapPlayedObjectIds, payedTours } = useAppSelector(
    (store) => store.catalogStore,
  )

  const dispatch = useAppDispatch()
  const { initGeo } = useGeolocation()
  const { t, i18n } = useTranslation('root')
  const location = useLocation()

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_KEY as string,
    libraries,
  })

  const isLanding = location.pathname.includes('begin') || location.pathname.includes('welcome')
  const isRealBrowser = !('ontouchstart' in window || navigator.maxTouchPoints)
  const [initialPageViewSent, setInitialPageViewSent] = useState(false)

  useEffect(() => {
    {
      const searchParams = new URLSearchParams(location.search)
      const campaign = searchParams.get('c')

      const id = Cookies.get('userID') || nanoid()

      Cookies.set('userID', id)

      const tagVals = {
        user_id: id,
        locale: i18n.language,
        userLanguage: navigator.language,
        userAgent: navigator.userAgent,
        screen: `${window.innerWidth}x${window.innerHeight}`,
        ...(campaign && { campaign: campaign }),
      }
      ReactGA.gtag('set', tagVals)

      ReactGA.initialize('G-XN9GSJ81RG')

      timer.current = setInterval(() => {
        ReactGA.event({
          category: 'session',
          action: 'WA_SessionRunning',
        })
      }, 5 * 60 * 1000)

      return () => {
        clearInterval(timer.current as NodeJS.Timeout)
      }
    }
  }, [])

  useEffect(() => {
    const pageLoadDelay = 6000 // 6 seconds (adjust as needed)

    if (isRealBrowser) {
      if (!initialPageViewSent) {
        setInitialPageViewSent(true)
      } else {
        const timer = setTimeout(() => {
          ReactGA.send({
            hitType: 'pageview',
            page: location.pathname + location.search,
          })
        }, pageLoadDelay)

        return () => {
          clearTimeout(timer) // Clear the timer if the component unmounts before the delay
        }
      }
    }
  }, [location])

  useEffect(() => {
    if (cleverMapPlayedObjectIds.length >= 3 && payedTours.length == 0) {
      if (modal !== 'payment') {
        ReactGA.event({
          category: 'payment',
          action: 'WA_PaymentRequested',
        })
      }
    }
  }, [cleverMapPlayedObjectIds, payedTours, modal])

  const timer: { current: NodeJS.Timeout | null } = useRef(null)

  {
    if (!isLanding) {
      //TODO: for some reason this is called multiple times, need to fix it
      //Retrieve geolocation - only if we are not on landing page
      initGeo()
    }
  }

  if (!isLanding && (!isLoaded || interestPoints.length === 0)) {
    return (
      <HelmetProvider>
        {/* {!isLanding && <Header />} */}
        <Header />

        {<Loader theme="page" active={!isLoaded} />}

        <PaymentModal />
        <CleverMapAccessModal />
        <FeedbackModal />
        <ToastContainer />
        <TutorialPrompt />
        <TutorialSlides />
        <FullScreenImage />
        {!Capacitor.isNativePlatform() && <LayoutCookie />}
      </HelmetProvider>
    )
  }

  return (
    <HelmetProvider>
      {!isLanding && (
        <Helmet>
          <title>WorldMuseum: Audio Map of Rome</title>
        </Helmet>
      )}

      {!isLanding && <Header />}

      {
        <Routes>
          {/* <Route path="/" element={<WelcomePage />} /> */}
          <Route path="/" element={<Navigate to="/map" replace />} />
          <Route path="/begin" element={<Navigate to="/" replace />} />
          <Route path="/map" element={<HomePage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      }
      <SpecialOffer />
      <PaymentModal />
      <CleverMapAccessModal />
      <FeedbackModal />
      <TutorialSlides />
      <TutorialPrompt />
      <FullScreenImage />
      <ToastContainer />
      {!Capacitor.isNativePlatform() && <LayoutCookie />}
      {/* <Assistent /> */}
    </HelmetProvider>
  )
}

export default App
